/**
 * Equipment Register PDF Export Utility
 * Professional compliance-grade PDF export for Equipment Register
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Format date for PDF display
 */
function formatDate(dateValue) {
  if (!dateValue) return '-';

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    return '-';
  }
}

/**
 * Generate Equipment Register PDF
 * @param {Array} items - Equipment items to export
 * @param {Object} vessel - Current vessel info
 * @param {Object} options - Export options
 */
export function generateEquipmentPDF(items, vessel = {}, options = {}) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 15;

  // ========================================
  // HEADER SECTION
  // ========================================

  // Company/Vessel Name
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(vessel.name || 'Equipment Register', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;

  // Document Title
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Equipment Register', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  // Vessel Details Box
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const vesselDetails = [
    `Vessel: ${vessel.name || 'Unknown'}`,
    `Category: ${vessel.category || '-'}`,
    `Type: ${vessel.vesselType || '-'}`,
    `Export Date: ${new Date().toLocaleDateString('en-GB')}`,
    `Total Items: ${items.length}`
  ];

  const detailsBoxY = yPosition;
  const detailsBoxHeight = 20;

  // Draw details box
  doc.setDrawColor(200);
  doc.setFillColor(245, 245, 245);
  doc.rect(15, detailsBoxY, pageWidth - 30, detailsBoxHeight, 'FD');

  // Add details text
  yPosition = detailsBoxY + 5;
  vesselDetails.forEach((detail, index) => {
    const xOffset = index < 3 ? 20 : (pageWidth / 2) + 10;
    const yOffset = index < 3 ? yPosition + (index * 5) : yPosition + ((index - 3) * 5);
    doc.text(detail, xOffset, yOffset);
  });

  yPosition = detailsBoxY + detailsBoxHeight + 8;

  // ========================================
  // EQUIPMENT TABLE
  // ========================================

  // Prepare table data
  const tableData = items.map(item => [
    item.category || '-',
    item.name || '-',
    item.type || '-',
    item.location || '-',
    item.serialNumber || '-',
    item.manufacturer || '-',
    item.model || '-',
    formatDate(item.installDate),
    formatDate(item.lastServiceDate),
    formatDate(item.nextServiceDate),
    item.status || 'Active'
  ]);

  // Generate table
  autoTable(doc, {
    startY: yPosition,
    head: [[
      'Category',
      'Name',
      'Type',
      'Location',
      'Serial No.',
      'Manufacturer',
      'Model',
      'Install',
      'Last Service',
      'Next Service',
      'Status'
    ]],
    body: tableData,
    styles: {
      fontSize: 7,
      cellPadding: 2,
      overflow: 'linebreak',
      halign: 'left'
    },
    headStyles: {
      fillColor: [55, 65, 81], // gray-700
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 7
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251] // gray-50
    },
    columnStyles: {
      0: { cellWidth: 20 }, // Category
      1: { cellWidth: 35 }, // Name
      2: { cellWidth: 20 }, // Type
      3: { cellWidth: 25 }, // Location
      4: { cellWidth: 25 }, // Serial
      5: { cellWidth: 30 }, // Manufacturer
      6: { cellWidth: 25 }, // Model
      7: { cellWidth: 18 }, // Install Date
      8: { cellWidth: 18 }, // Last Service
      9: { cellWidth: 18 }, // Next Service
      10: { cellWidth: 15 }  // Status
    },
    margin: { left: 15, right: 15 },
    didDrawPage: function (data) {
      // Footer on each page
      const pageCount = doc.internal.getNumberOfPages();
      const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Page ${pageCurrent} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      doc.text(
        'Equipment Register Export',
        15,
        pageHeight - 10
      );

      doc.text(
        new Date().toLocaleDateString('en-GB'),
        pageWidth - 15,
        pageHeight - 10,
        { align: 'right' }
      );
    }
  });

  // ========================================
  // SUMMARY SECTION (on last page)
  // ========================================

  const finalY = doc.lastAutoTable.finalY + 10;

  if (finalY + 30 < pageHeight - 20) {
    // Room on current page
    addSummarySection(doc, items, finalY, pageWidth);
  } else {
    // New page for summary
    doc.addPage();
    addSummarySection(doc, items, 15, pageWidth);
  }

  return doc;
}

/**
 * Add summary section to PDF
 */
function addSummarySection(doc, items, startY, pageWidth) {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Equipment Summary', 15, startY);

  // Calculate statistics
  const categoryCount = {};
  const typeCount = {};
  let needsServiceCount = 0;

  items.forEach(item => {
    // Count by category
    const cat = item.category || 'Other';
    categoryCount[cat] = (categoryCount[cat] || 0) + 1;

    // Count by type
    const type = item.type || 'Unknown';
    typeCount[type] = (typeCount[type] || 0) + 1;

    // Count items needing service
    if (item.nextServiceDate) {
      const nextService = new Date(item.nextServiceDate);
      const today = new Date();
      if (nextService <= today) {
        needsServiceCount++;
      }
    }
  });

  // Summary stats
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  let summaryY = startY + 8;

  // Category breakdown
  doc.setFont('helvetica', 'bold');
  doc.text('By Category:', 15, summaryY);
  summaryY += 5;

  doc.setFont('helvetica', 'normal');
  Object.entries(categoryCount)
    .sort((a, b) => b[1] - a[1])
    .forEach(([category, count]) => {
      doc.text(`  ${category}: ${count}`, 15, summaryY);
      summaryY += 4;
    });

  // Service status
  summaryY += 3;
  doc.setFont('helvetica', 'bold');
  doc.text('Service Status:', 15, summaryY);
  summaryY += 5;

  doc.setFont('helvetica', 'normal');
  if (needsServiceCount > 0) {
    doc.setTextColor(220, 38, 38); // red-600
    doc.text(`  ⚠ ${needsServiceCount} item(s) require service`, 15, summaryY);
    doc.setTextColor(0, 0, 0);
  } else {
    doc.setTextColor(22, 163, 74); // green-600
    doc.text(`  ✓ All equipment up to date`, 15, summaryY);
    doc.setTextColor(0, 0, 0);
  }
}

/**
 * Download equipment register as PDF
 * @param {Array} items - Equipment items to export
 * @param {Object} vessel - Current vessel info
 * @param {Object} options - Export options
 */
export function downloadEquipmentPDF(items, vessel = {}, options = {}) {
  const doc = generateEquipmentPDF(items, vessel, options);

  const filename = `equipment_register_${vessel.name || 'export'}_${new Date().toISOString().split('T')[0]}.pdf`;

  doc.save(filename);
}
